import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Plus, 
  Edit, 
  Trash2,
  Loader2, 
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FundingSourcesPage = () => {
  const [fundingSources, setFundingSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [deletingSource, setDeletingSource] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    code: '',
    is_active: true
  });

  useEffect(() => {
    fetchFundingSources();
  }, []);

  const fetchFundingSources = async () => {
    try {
      const response = await axios.get(`${API}/admin/funding-sources`);
      setFundingSources(response.data.funding_sources || []);
    } catch (error) {
      toast.error('Failed to load funding sources');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (source = null) => {
    if (source) {
      setEditingSource(source);
      setFormData({
        id: source.id,
        name: source.name,
        description: source.description || '',
        code: source.code || '',
        is_active: source.is_active !== false
      });
    } else {
      setEditingSource(null);
      setFormData({
        id: '',
        name: '',
        description: '',
        code: '',
        is_active: true
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingSource) {
        await axios.put(`${API}/admin/funding-sources/${editingSource.id}`, formData);
        toast.success('Funding source updated successfully');
      } else {
        const sourceId = formData.id || formData.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        await axios.post(`${API}/admin/funding-sources`, {
          ...formData,
          id: sourceId
        });
        toast.success('Funding source created successfully');
      }
      setShowDialog(false);
      fetchFundingSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save funding source');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingSource) return;
    setSaving(true);

    try {
      await axios.delete(`${API}/admin/funding-sources/${deletingSource.id}`);
      toast.success('Funding source deleted successfully');
      setShowDeleteDialog(false);
      setDeletingSource(null);
      fetchFundingSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete funding source');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (source) => {
    try {
      await axios.put(`${API}/admin/funding-sources/${source.id}`, {
        is_active: !source.is_active
      });
      toast.success(`Funding source ${!source.is_active ? 'activated' : 'deactivated'}`);
      fetchFundingSources();
    } catch (error) {
      toast.error('Failed to update funding source');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="funding-sources-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="funding-sources-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Funding Sources</h1>
          <p className="text-gray-500">Configure funding sources for program enrollments</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-funding-source-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Funding Source
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5" />
            Funding Sources ({fundingSources.length})
          </CardTitle>
          <CardDescription>
            Manage funding sources used in program enrollments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fundingSources.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No funding sources configured</p>
              <p className="text-sm">Click &quot;Add Funding Source&quot; to create one</p>
            </div>
          ) : (
            <div className="divide-y">
              {fundingSources.map((source) => (
                <div 
                  key={source.id} 
                  className="py-4 flex items-center justify-between"
                  data-testid={`funding-source-row-${source.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{source.name}</span>
                      {source.code && (
                        <Badge variant="outline" className="text-xs">{source.code}</Badge>
                      )}
                      <Badge variant={source.is_active ? "default" : "secondary"}>
                        {source.is_active ? (
                          <><CheckCircle className="w-3 h-3 mr-1" />Active</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" />Inactive</>
                        )}
                      </Badge>
                    </div>
                    {source.description && (
                      <p className="text-sm text-gray-500 mt-1">{source.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleToggleActive(source)}
                      data-testid={`toggle-funding-source-${source.id}`}
                    >
                      {source.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleOpenDialog(source)}
                      data-testid={`edit-funding-source-${source.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        setDeletingSource(source);
                        setShowDeleteDialog(true);
                      }}
                      data-testid={`delete-funding-source-${source.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSource ? 'Edit Funding Source' : 'Add Funding Source'}</DialogTitle>
            <DialogDescription>
              Configure funding source details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., SNAP 50/50"
                required
                data-testid="funding-source-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                placeholder="e.g., SNAP-50"
                data-testid="funding-source-code-input"
              />
              <p className="text-xs text-gray-500">Optional short code for reporting</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of this funding source"
                rows={2}
                data-testid="funding-source-description-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                data-testid="funding-source-active-switch"
              />
              <Label>Active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-funding-source-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingSource ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Funding Source
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deletingSource?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={saving}
              data-testid="confirm-delete-funding-source-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FundingSourcesPage;
